import "react-calendar/dist/Calendar.css";
import Layout from "../../components/layoutConfig/layout";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import { setupAPIclient } from "../../services/setupAPIclient";
import LocalStorage from "../../helpers/localstorage";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import { useState, useEffect, FormEvent } from "react";
import { useRef } from "react";
import { motion } from "framer-motion";
import {
  FiTrash2,
  FiPlusCircle,
  FiCheckCircle,
  FiSearch,
  FiBox,
  FiHash,
  FiEdit,
  FiList,
  FiX,
} from "react-icons/fi";
import GuidedTutorial from "../../components/common/GuidedTutorial";

interface FormRegionInput {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

function RegionForm() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const [data, setData] = useState<FormRegionInput[]>([]);
  const [filterText, setFilterText] = useState("");
  const [pending, setPending] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [formRegiao, setFormRegiao] = useState({ name: "" });
  const nameRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const ApiClient = setupAPIclient(token);

  const handleGetDataInput = async () => {
    try {
      setPending(true);
      const response = await ApiClient.post<FormRegionInput[]>("/management/get/region");
      setData(response.data || []);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setPending(false);
    }
  };

  const handleCreateOrUpdate = async (e: FormEvent) => {
    e.preventDefault();

    if (!formRegiao.name.trim()) {
      toast.warn("Preencha o nome da região");
      return;
    }

    try {
      if (editId) {
        await ApiClient.post("/management/update/region", {
          id: editId,
          name: formRegiao.name,
        });
        toast.success("Região atualizada com sucesso");
      } else {
        await ApiClient.post("/management/create/region", { ...formRegiao });
        toast.success("Região cadastrada com sucesso");
      }

      setEditId(null);
      setFormRegiao({ name: "" });
      handleGetDataInput();
      window.dispatchEvent(new Event("management:onboarding-updated"));
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error("Erro ao salvar região");
    }
  };

  const handleEdit = (row: FormRegionInput) => {
    setEditId(row.id);
    setFormRegiao({ name: row.name || "" });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setFormRegiao({ name: "" });
  };

  const handleDelete = async (row: FormRegionInput) => {
    if (!window.confirm(`Tem certeza que deseja deletar a região ${row.name}?`)) return;
    try {
      const response = await ApiClient.post("/management/delete/region", { id: row.id });
      if (!response.data.success) {
        toast.warning(`Não é possível deletar. Existem dados associados à região ${row.name}`);
      } else {
        toast.success("Registro deletado com sucesso");
        if (editId === row.id) {
          handleCancelEdit();
        }
        handleGetDataInput();
        window.dispatchEvent(new Event("management:onboarding-updated"));
      }
    } catch (error) {
      console.error("Erro ao deletar:", error);
      toast.warning(`Não é possível deletar. Existem dados associados à região ${row.name}`);
    }
  };

  useEffect(() => {
    handleGetDataInput();
  }, []);

  const iconColor = "#5dade2";

  const columns: TableColumn<FormRegionInput>[] = [
    {
      name: (
        <div className="d-flex align-items-center gap-1">
          <FiHash color={iconColor} size={18} /> ID
        </div>
      ),
      selector: (row) => row.id,
      sortable: true,
      minWidth: "80px",
    },
    {
      name: (
        <div className="d-flex align-items-center gap-1">
          <FiBox color={iconColor} size={18} /> Nome
        </div>
      ),
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2 fw-semibold">
          <FiBox color={iconColor} /> {row.name}
        </div>
      ),
      minWidth: "200px",
    },
    {
      name: "Ações",
      cell: (row) => (
        <div className="d-flex gap-2 justify-content-center flex-wrap">
          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            className="btn btn-sm modern-primary-btn"
            onClick={() => handleEdit(row)}
          >
            <FiEdit />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row)}
          >
            <FiTrash2 />
          </motion.button>
        </div>
      ),
      center: true,
      minWidth: "140px",
    },
  ];

  const filteredItems = data.filter((item) => item.name?.toLowerCase().includes(filterText.toLowerCase()));

  return (
    <Layout>
      <GuidedTutorial
        storageKey="management_tutorial_region_v1"
        enabled={localStorage.getStep() === "2"}
        nextSystemStep="3"
        steps={[
          { targetRef: nameRef as any, title: "Nome da região", description: "Nesta tela opcional, basta preencher o nome da região." },
          { targetRef: submitRef as any, title: "Salvar região", description: "Clique em cadastrar para registrar a nova região." },
          { targetRef: searchRef as any, title: "Pesquisar", description: "Use a busca para localizar regiões já cadastradas." },
          { targetRef: tableRef as any, title: "Lista de regiões", description: "Aqui você acompanha, edita ou remove as regiões existentes." },
        ]}
      />
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="d-flex flex-wrap gap-3 mb-4">
          <div className="modern-card p-4 flex-grow-1">
            <h4 className="d-flex align-items-center gap-2 mb-2">
              <FiBox color={iconColor} size={22} /> Configuração de Região
            </h4>
            <p className="mb-0" style={{ color: "#64748b" }}>
              Organize suas regiões com um fluxo de edição padrão e consistente.
            </p>
          </div>

          <div className="modern-card p-4" style={{ minWidth: 210 }}>
            <div className="d-flex align-items-center gap-2">
              <FiList color={iconColor} size={22} />
              <div>
                <div style={{ color: "#64748b", fontSize: 13 }}>Total de Regiões</div>
                <strong style={{ fontSize: 28 }}>{data.length}</strong>
              </div>
            </div>
          </div>
        </div>

        <motion.div className="modern-card p-4 mb-4" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <h5 className="mb-3 d-flex align-items-center gap-2">
            <FiPlusCircle color={iconColor} /> {editId ? "Editar Região" : "Nova Região"}
          </h5>

          <form onSubmit={handleCreateOrUpdate}>
            <div className="row align-items-end g-3">
              <div className="col-md-6 col-lg-4">
                <label className="form-label">Nome da Região</label>
                <input
                  ref={nameRef}
                  type="text"
                  className="form-control"
                  placeholder="Digite o nome da região"
                  value={formRegiao.name}
                  onChange={(e) => setFormRegiao({ name: e.target.value })}
                />
              </div>

              <div className="col-md-6 col-lg-4 d-flex gap-2">
                <motion.button ref={submitRef} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" className="btn modern-primary-btn flex-fill">
                  <FiCheckCircle className="me-1" /> {editId ? "Salvar" : "Cadastrar"}
                </motion.button>

                {editId && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleCancelEdit}
                  >
                    <FiX className="me-1" /> Cancelar
                  </motion.button>
                )}
              </div>
            </div>
          </form>
        </motion.div>

        <div className="mb-3">
          <div className="input-group" style={{ maxWidth: "320px" }}>
            <span className="input-group-text bg-white">
              <FiSearch color={iconColor} />
            </span>
            <input
              ref={searchRef}
              type="text"
              className="form-control"
              placeholder="Pesquisar região..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>
        </div>

        <div ref={tableRef} className="modern-card p-2 p-md-3">
          <DataTable
            columns={columns}
            data={filteredItems}
            progressPending={pending}
            pagination
            highlightOnHover
            pointerOnHover
            responsive
            striped
            dense
            noHeader
          />
        </div>
      </motion.div>
    </Layout>
  );
}

export default RegionForm;
